import { useQuery, useMutation } from '@apollo/client';
import { GET_BOOKMARKED_QUESTIONS_FOR_USER } from '../graphql/getBookmarkedQuestionsForUser';
import { BOOKMARK_QUESTION } from '../graphql/bookMarkQuestion';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { CommonStyles } from '../styles/CommonStyles';
import {
  Question,
  SelectedOption,
  UserQuestion,
} from '../__generated__/graphql';
import { useState, useEffect } from 'react';
import { QuestionCardStyles } from '../styles/QuestionCardStyles';
import QuestionCard from '../components/QuestionCard';
import { QuizStyles } from '../styles/QuizStyles';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { TAGS } from '../components/OrganCard';
import { tagToColor } from '../types/Tag';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export const Bookmarks = () => {
  const [currIndex, setCurrIndex] = useState<number>(0);
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [filteredUserQuestions, setFilteredUserQuestions] = useState<
    UserQuestion[]
  >([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(true);

  const { data, loading } = useQuery(GET_BOOKMARKED_QUESTIONS_FOR_USER);
  const [bookmarkQuestion] = useMutation(BOOKMARK_QUESTION);

  const convertToUserQuestion = (question: Question): UserQuestion => {
    return {
      questionId: question.id,
      question: question,
      selectedOption: {
        desc: null,
        isAnswer: null,
      },
    };
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [tag, ...prev];
      }
    });
  };

  const handleClearFilters = () => {
    setSelectedTags([]);
  };

  const handleBookmarkToggle = async () => {
    if (!filteredUserQuestions || filteredUserQuestions.length === 0) return;

    const currentQuestionId = filteredUserQuestions[currIndex]?.question.id;
    if (!currentQuestionId) return;

    try {
      await bookmarkQuestion({
        variables: { questionId: currentQuestionId },
        refetchQueries: [{ query: GET_BOOKMARKED_QUESTIONS_FOR_USER }],
      });

      if (currIndex === filteredUserQuestions.length - 1 && currIndex > 0) {
        setCurrIndex(currIndex - 1);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleClearAnswers = () => {
    if (!filteredUserQuestions || filteredUserQuestions.length === 0) return;

    const newUserQuestions = userQuestions.map((userQuestion) => {
      if (
        userQuestion.questionId === filteredUserQuestions[currIndex].questionId
      ) {
        return {
          ...userQuestion,
          selectedOption: {
            desc: null,
            isAnswer: null,
          },
        };
      }
      return userQuestion;
    });
    setUserQuestions(newUserQuestions);
  };

  useEffect(() => {
    if (data && data.getBookmarkedQuestionsForUser) {
      const questions = data.getBookmarkedQuestionsForUser.map((q: Question) =>
        convertToUserQuestion(q),
      );
      setUserQuestions(questions);
      setFilteredUserQuestions(questions);
      setCurrIndex(0); // Reset to first question on initial load
    }
  }, [data]);

  useEffect(() => {
    if (selectedTags.length === 0) {
      setFilteredUserQuestions(userQuestions);
    } else {
      const filtered = userQuestions.filter((userQuestion) =>
        selectedTags.includes(userQuestion.question.tag),
      );

      // Sort filtered questions based on the order of selected tags
      const sortedFiltered = filtered.sort((a, b) => {
        const aIndex = selectedTags.indexOf(a.question.tag);
        const bIndex = selectedTags.indexOf(b.question.tag);
        return aIndex - bIndex;
      });

      setFilteredUserQuestions(sortedFiltered);
      setCurrIndex(0);
    }
  }, [selectedTags, userQuestions]);

  if (loading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  if (
    !data ||
    !data.getBookmarkedQuestionsForUser ||
    userQuestions.length === 0
  ) {
    return (
      <Box sx={QuizStyles.noContent}>There are no bookmarked questions.</Box>
    );
  }

  const handleUpdateBookmarkOption = (
    questionId: string,
    selectedOption: SelectedOption,
  ) => {
    const newUserQuestions = userQuestions.map((userQuestion) => {
      if (userQuestion.questionId === questionId) {
        return { ...userQuestion, selectedOption };
      }
      return userQuestion;
    });
    setUserQuestions(newUserQuestions);
  };

  return (
    <Box sx={{ ...QuestionCardStyles.root }}>
      {/* Filter Section - Outside the white container */}
      <Box
        sx={{
          mb: 3,
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
          width: '95%',
          maxWidth: '1000px',
          overflow: 'hidden',
        }}
      >
        {/* Filter Header with Toggle */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        >
          <Typography
            variant="h6"
            sx={{ color: 'rgb(65, 69, 74)', userSelect: 'none' }}
          >
            Filter by Organ
          </Typography>
          {isFilterExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {/* Collapsible Filter Content */}
        <Box
          sx={{
            maxHeight: isFilterExpanded ? '300px' : '0px',
            opacity: isFilterExpanded ? 1 : 0,
            transition: 'all 0.3s ease-in-out',
            overflow: 'hidden',
            px: 2,
            pb: isFilterExpanded ? 2 : 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
              alignItems: 'center',
            }}
          >
            {TAGS.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onClick={() => handleTagToggle(tag)}
                size="small"
                sx={{
                  backgroundColor: selectedTags.includes(tag)
                    ? tagToColor[tag] || '#BDBDBD'
                    : '#e0e0e0',
                  color: selectedTags.includes(tag) ? '#FFF' : '#666',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  height: '24px',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0.5,
                  },
                  '&:hover': {
                    backgroundColor: selectedTags.includes(tag)
                      ? tagToColor[tag] || '#BDBDBD'
                      : '#d0d0d0',
                  },
                }}
              />
            ))}
          </Box>
          {selectedTags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                sx={{
                  color: 'rgb(65, 69, 74)',
                  borderColor: 'rgb(65, 69, 74)',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  minHeight: '24px',
                }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
            {filteredUserQuestions.length} of {userQuestions.length} questions
          </Typography>
        </Box>
      </Box>

      {filteredUserQuestions.length === 0 && (
        <Box sx={QuizStyles.noContent}>
          No bookmarked questions match the selected filters.
        </Box>
      )}

      {filteredUserQuestions.length > 0 && (
        <Box sx={{ ...QuestionCardStyles.container, position: 'relative' }}>
          <IconButton
            onClick={handleBookmarkToggle}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
          >
            <BookmarkIcon
              sx={{ color: 'rgba(245, 52, 75, 0.9)', fontSize: 32 }}
            />
          </IconButton>

          <QuestionCard
            key={filteredUserQuestions[currIndex].question.id}
            userQuestion={filteredUserQuestions[currIndex]}
            index={currIndex}
            onSelectedOption={(questionId, selectedOption) => {
              handleUpdateBookmarkOption(questionId, selectedOption);
            }}
            disabled={
              filteredUserQuestions[currIndex]?.selectedOption?.desc !== null
            }
            onClearAnswers={handleClearAnswers}
            showTag={true}
          />
          <Box sx={QuizStyles.buttonContainer}>
            <Button
              sx={[QuizStyles.button, QuizStyles.backButton]}
              disabled={currIndex === 0}
              variant="outlined"
              onClick={() =>
                setCurrIndex((prevCurrIndex) =>
                  prevCurrIndex > 0 ? prevCurrIndex - 1 : prevCurrIndex,
                )
              }
            >
              Back
            </Button>
            <Button
              sx={[QuizStyles.button, QuizStyles.nextButton]}
              variant="contained"
              disabled={currIndex === filteredUserQuestions.length - 1}
              onClick={() =>
                setCurrIndex((prevCurrIndex) =>
                  prevCurrIndex < filteredUserQuestions.length - 1
                    ? prevCurrIndex + 1
                    : prevCurrIndex,
                )
              }
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
